import { useState, useEffect, useCallback } from 'react';
import type { DriveItem } from '@/mocks/driveFiles';
import { formatFileSize } from '@/mocks/driveFiles';

export interface LightboxImage {
  item: DriveItem;
  src: string;
}

interface ImageViewerProps {
  images: LightboxImage[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export default function ImageViewer({ images, currentIndex, onClose, onNavigate }: ImageViewerProps) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const current = images[currentIndex];

  const goNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setLoaded(false);
      setError(false);
      onNavigate(currentIndex + 1);
    }
  }, [currentIndex, images.length, onNavigate]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setLoaded(false);
      setError(false);
      onNavigate(currentIndex - 1);
    }
  }, [currentIndex, onNavigate]);

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, goPrev, goNext]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/92 backdrop-blur-md"></div>

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer"
        aria-label="닫기"
      >
        <i className="ri-close-line text-xl"></i>
      </button>

      {/* Counter - top center */}
      {images.length > 1 && (
        <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10 px-4 py-1.5 rounded-full bg-white/10 text-white/80 text-sm font-medium backdrop-blur-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Prev button */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 md:left-8 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer group"
          aria-label="이전 이미지"
        >
          <i className="ri-arrow-left-s-line text-2xl group-hover:-translate-x-0.5 transition-transform"></i>
        </button>
      )}

      {/* Next button */}
      {currentIndex < images.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 md:right-8 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer group"
          aria-label="다음 이미지"
        >
          <i className="ri-arrow-right-s-line text-2xl group-hover:translate-x-0.5 transition-transform"></i>
        </button>
      )}

      {/* Image container */}
      <div
        className="relative z-[1] max-w-[92vw] max-h-[82vh] flex items-center justify-center select-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading spinner */}
        {!loaded && !error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5">
              <i className="ri-loader-4-line text-3xl text-white/50 animate-spin"></i>
            </span>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex flex-col items-center gap-4 text-white/50 py-16 px-8">
            <span className="w-20 h-20 flex items-center justify-center rounded-2xl bg-white/5">
              <i className="ri-image-add-line text-4xl"></i>
            </span>
            <p className="text-sm font-medium">이미지를 불러올 수 없습니다</p>
            <p className="text-xs text-white/30">{current.item.name}</p>
          </div>
        )}

        {/* Actual image */}
        <img
          src={current.src}
          alt={current.item.name}
          className={`max-w-full max-h-[82vh] object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${loaded && !error ? 'opacity-100' : 'opacity-0 absolute'}`}
          onLoad={() => { setLoaded(true); setError(false); }}
          onError={() => { setError(true); setLoaded(false); }}
          draggable={false}
        />
      </div>

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-center gap-4 px-6 py-4 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
        <span className="text-white/90 text-sm font-medium max-w-[40vw] truncate">{current.item.name}</span>
        {current.item.size != null && (
          <>
            <span className="text-white/30 text-xs select-none">|</span>
            <span className="text-white/55 text-sm">{formatFileSize(current.item.size)}</span>
          </>
        )}
      </div>
    </div>
  );
}